import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Plus, ArrowRight, User, Trophy, Flame, Target } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';

const Friends = () => {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFriends();
  }, []);

  const loadFriends = async () => {
    try {
      const response = await api.get('/friends');
      setFriends(response.data);
    } catch {
      // Silently fail — user will see empty state
    } finally {
      setLoading(false);
    }
  };

  const handleAddFriend = async () => {
    if (!email) {
      toast.error('Please enter an email');
      return;
    }

    try {
      await api.post('/friends/add', null, { params: { friend_email: email } });
      toast.success('Friend request sent.');
      setEmail('');
      loadFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const handleChallenge = (friendId) => {
    navigate(`/challenges/create?friend=${friendId}`);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#1D1D1F] mb-3 tracking-tight">
            Friends
          </h1>
          <p className="text-xl text-[#86868B]">
            Connect with friends and challenge them to practice battles
          </p>
        </div>

        {/* Add Friend */}
        <Card className="p-8 mb-8 bg-gradient-to-br from-[#E8F4FD] to-white rounded-3xl shadow-apple-lg border border-blue-100">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-[#0071E3] rounded-2xl flex items-center justify-center">
              <Plus className="text-white" size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-[#1D1D1F]">Add New Friend</h2>
              <p className="text-[#86868B]">Enter their email to send a friend request</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Input
              type="email"
              placeholder="friend@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 h-12 rounded-xl bg-white border-transparent focus:border-[#0071E3]"
            />
            <Button
              onClick={handleAddFriend}
              className="rounded-full bg-[#0071E3] hover:bg-[#0077ED] text-white px-8 shadow-lg shadow-blue-500/30"
            >
              <Plus className="mr-2" size={18} />
              Add Friend
            </Button>
          </div>
        </Card>

        {/* Friends List */}
        {loading ? (
          <LoadingSpinner text="Loading friends..." />
        ) : friends.length === 0 ? (
          <Card className="p-12 text-center bg-white rounded-3xl shadow-apple border border-gray-100">
            <Users className="text-[#86868B] mx-auto mb-4" size={64} />
            <h3 className="text-2xl font-heading font-bold text-[#1D1D1F] mb-2">No friends yet</h3>
            <p className="text-[#86868B]">Add friends to start practicing together.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {friends.map((friend) => (
              <Card
                key={friend.id}
                className="p-6 bg-white rounded-2xl shadow-apple hover:shadow-apple-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#0071E3] to-[#0055B8] rounded-2xl flex items-center justify-center">
                      <User className="text-white" size={28} />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-[#1D1D1F] text-lg">{friend.name}</h3>
                      <p className="text-sm text-[#86868B]">{friend.email}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mb-4">
                  <div className="flex-1 bg-[#F5F5F7] rounded-xl p-3 text-center">
                    <Trophy className="text-[#0071E3] mx-auto mb-1" size={20} />
                    <p className="text-sm font-bold text-[#1D1D1F]">{friend.points}</p>
                    <p className="text-xs text-[#86868B]">Points</p>
                  </div>
                  <div className="flex-1 bg-[#F5F5F7] rounded-xl p-3 text-center">
                    <Flame className="text-[#FF9F0A] mx-auto mb-1" size={20} />
                    <p className="text-sm font-bold text-[#1D1D1F]">{friend.streak}</p>
                    <p className="text-xs text-[#86868B]">Streak</p>
                  </div>
                </div>

                <Button
                  onClick={() => handleChallenge(friend.id)}
                  className="w-full rounded-full bg-[#0071E3] hover:bg-[#0077ED] text-white shadow-lg shadow-blue-500/20"
                >
                  <Target className="mr-2" size={18} />
                  Challenge
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Friends;
